import classNames from 'classnames';
import { ReactNode } from 'react';

interface Props {
  /** Store glyph (App Store, Google Play, macOS…). */
  icon: ReactNode;
  /** Small line above the store name (e.g. "Download on the"). */
  small: string;
  /** Store name (e.g. "App Store"). */
  big: string;
  href?: string;
  /** Open the link in a new browser tab. */
  newTab?: boolean;
  /** Render greyed-out and non-interactive (platform not yet available). */
  disabled?: boolean;
  className?: string;
}

// White pill "store" button used across the platform download rows.
// Matches the marketing design (.store): white surface, subtle lift on hover.
export const StoreBadge = ({
  icon,
  small,
  big,
  href = '#',
  newTab,
  disabled,
  className,
}: Props) => {
  const content = (
    <>
      <span className={styles.icon}>{icon}</span>
      <span className={styles.text}>
        <small className={styles.small}>{small}</small>
        <b className={styles.big}>{big}</b>
      </span>
    </>
  );

  if (disabled) {
    return (
      <span
        aria-disabled="true"
        className={classNames(styles.base, styles.disabled, className)}
      >
        {content}
      </span>
    );
  }

  return (
    <a
      href={href}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className={classNames(styles.base, styles.interactive, className)}
    >
      {content}
    </a>
  );
};

const styles = {
  // Hairline border makes the white pill legible on the light page; the navy
  // rows keep the original borderless look.
  base: [
    'inline-flex items-center gap-[11px] rounded-[13px] bg-white px-[18px] py-2.5',
    'border border-mf-gray-300 marketing-dark:border-0',
  ].join(' '),
  interactive: [
    'transition-[transform,border-color,box-shadow] hover:-translate-y-0.5',
    'hover:border-mf-gray-500 hover:shadow-[0_8px_22px_rgba(20,30,60,0.12)]',
    'marketing-dark:hover:shadow-[0_14px_30px_rgba(0,0,0,0.32)]',
  ].join(' '),
  // Faded + non-clickable — reads as "not available yet".
  disabled: 'cursor-not-allowed opacity-40',
  icon: 'flex-shrink-0 [&>svg]:h-[26px] [&>svg]:w-[26px] [&>svg]:fill-[#161E3A]',
  text: 'text-left leading-[1.1]',
  small: 'block text-[10px] font-medium text-[#6c727e]',
  big: 'text-[15px] font-bold text-[#161E3A]',
};
